import {
  type DomainCommandContext,
  DomainFault,
  type DomainModule,
  type DomainReadContext,
  paginate,
  type QueryInput,
} from '../../contracts';
import type { Command } from '../../service';
import {
  type Entity,
  type Evidence,
  type Role,
  type Snapshot,
  seedUuid,
} from '../../types';
import type {
  CommitmentCandidate,
  FeedbackRecord,
  FeedbackSentence,
  FeedbackVersion,
  ServiceMetrics,
  StudentServiceProfile,
} from './types';

export * from './types';

const staff: Role[] = [
  'studentService',
  'campusManager',
  'regionManager',
  'groupOperator',
];
const managers: Role[] = ['campusManager', 'regionManager', 'groupOperator'];
const key = 'studentServiceV1';
const fail = (message: string, code = 'VALIDATION'): never => {
  throw new DomainFault(code, message);
};
const need = (value: string | undefined, name: string) =>
  value?.trim() || fail(`请填写${name}`);
export function commitment(row: Entity): CommitmentCandidate | undefined {
  if (row.kind !== 'followUp' || !row.fields?.[key]) return undefined;
  return JSON.parse(row.fields[key]) as CommitmentCandidate;
}
export function feedbackRecord(row: Entity): FeedbackRecord | undefined {
  if (row.kind !== 'feedback' || !row.fields?.[key]) return undefined;
  return JSON.parse(row.fields[key]) as FeedbackRecord;
}
function write(row: Entity, value: CommitmentCandidate | FeedbackRecord) {
  row.fields = { ...row.fields, [key]: JSON.stringify(value) };
}
function entity(
  ctx: DomainReadContext,
  uuid: string | undefined,
  kind: string,
) {
  const row = ctx.get(uuid);
  if (!row || row.kind !== kind)
    return fail('对象不存在或当前视角无权访问', 'FORBIDDEN');
  return row;
}
export function canonicalEvidence(
  ctx: Pick<DomainReadContext, 'get'>,
  ref: Evidence,
): Evidence {
  const recording = ctx.get(ref.recordingUuid);
  if (!recording || recording.kind !== 'recording')
    return fail('来源录音已不可访问', 'FORBIDDEN');
  const version = recording.versions?.find(
    (v) => v.uuid === ref.transcriptVersionUuid,
  );
  if (
    !version ||
    !Number.isFinite(ref.startMs) ||
    !Number.isFinite(ref.endMs) ||
    ref.startMs < 0 ||
    ref.endMs <= ref.startMs ||
    ref.endMs > (version.segments.at(-1)?.endMs || 0)
  )
    return fail('来源片段或转写版本无效', 'CONFLICT');
  const segments = version.segments.filter(
    (s) => s.startMs < ref.endMs && s.endMs > ref.startMs,
  );
  if (!segments.length) return fail('来源片段没有可引用内容', 'CONFLICT');
  return { ...ref, quote: segments.map((s) => s.text).join(' ') };
}
function profileSource(ctx: DomainReadContext, row: Entity) {
  const data = row.kind === 'feedback' ? feedbackRecord(row) : commitment(row);
  const id = data?.studentUuid || row.fields?.studentUuid;
  const student = entity(ctx, id, 'student');
  if (student.campus !== row.campus) return fail('学生与服务记录校区不一致');
  if (row.evidence) canonicalEvidence(ctx, row.evidence);
  return student;
}
function follow(ctx: DomainReadContext, uuid: string | undefined) {
  const row = entity(ctx, uuid, 'followUp');
  profileSource(ctx, row);
  if (!commitment(row))
    return fail('旧记录需完成服务资料迁移后再操作', 'CONFLICT');
  return row;
}
function owner(ctx: DomainReadContext, row: Entity) {
  if (
    !managers.includes(ctx.context.role) &&
    row.owner !== ctx.context.viewMemberUuid
  )
    return fail('只有负责人或组织管理人员可以处理', 'FORBIDDEN');
}
function states(row: Entity, allowed: string[]) {
  if (!allowed.includes(row.status))
    fail('当前状态不允许此操作，请刷新核实', 'INVALID_TRANSITION');
}
function date(value: string | undefined, name: string) {
  const text = need(value, name);
  if (!/(Z|[+-]\d{2}:\d{2})$/.test(text) || !Number.isFinite(Date.parse(text)))
    return fail(`${name}须为带时区的有效时间`);
  return text;
}
function preview(input: Command) {
  const at = Number(input.fields?.previewAt);
  if (
    !Number.isFinite(at) ||
    Date.now() - at > 900000 ||
    at > Date.now() + 5000
  )
    return fail('预览已过期，请重新打开', 'CONFLICT');
}
function save(
  ctx: DomainCommandContext,
  row: Entity,
  data: CommitmentCandidate | FeedbackRecord,
  title: string,
) {
  write(row, data);
  ctx.touch(row);
  ctx.notify(row, title);
  return row;
}
export function visibleFollowUps(ctx: DomainReadContext) {
  return ctx.list('followUp').filter((row) => {
    try {
      return !!commitment(row) && !!profileSource(ctx, row);
    } catch {
      return false;
    }
  });
}
function validFeedback(ctx: DomainReadContext, row: Entity) {
  profileSource(ctx, row);
  const data = feedbackRecord(row);
  if (!data) return false;
  // Any inaccessible historical citation withdraws the whole document, including previews/search.
  for (const v of data.versions)
    for (const s of v.sentences) {
      const source = entity(ctx, s.followUpUuid, 'followUp');
      const sourceStudent = profileSource(ctx, source);
      if (
        source.uuid !== row.sourceUuid ||
        sourceStudent.uuid !== data.studentUuid
      )
        return fail('反馈引用跨学生', 'FORBIDDEN');
      if (s.evidence) canonicalEvidence(ctx, s.evidence);
    }
  return true;
}
export function visibleFeedback(ctx: DomainReadContext) {
  return ctx.list('feedback').filter((row) => {
    try {
      return validFeedback(ctx, row);
    } catch {
      return false;
    }
  });
}
export function serviceMetrics(ctx: DomainReadContext): ServiceMetrics {
  const rows = visibleFollowUps(ctx),
    confirmed = rows.filter((r) => !!commitment(r)?.confirmedAt);
  const open = confirmed.filter((r) => r.status !== 'completed');
  return {
    confirmed: confirmed.length,
    completed: confirmed.filter((r) => r.status === 'completed').length,
    future: open.filter(
      (r) => r.dueAt && Date.parse(r.dueAt) >= Date.parse(ctx.now),
    ).length,
    overdue: open.filter(
      (r) => r.dueAt && Date.parse(r.dueAt) < Date.parse(ctx.now),
    ).length,
    missingDeadline: open.filter((r) => !r.dueAt).length,
    candidates: rows.filter(
      (r) =>
        ['candidate', 'draft'].includes(r.status) &&
        !commitment(r)?.confirmedAt,
    ).length,
    overdueDenominator: open.filter((r) => !!r.dueAt).length,
  };
}
export function serviceProfile(
  ctx: DomainReadContext,
  studentUuid: string,
): StudentServiceProfile {
  const student = entity(ctx, studentUuid, 'student');
  const commitments = visibleFollowUps(ctx).filter(
    (r) => commitment(r)?.studentUuid === studentUuid,
  );
  const lessons = ctx
    .list('lesson')
    .filter(
      (r) =>
        r.campus === student.campus &&
        (r.fields?.studentUuids || '').split(',').includes(studentUuid),
    );
  return {
    studentUuid,
    campus: student.campus,
    advisorMemberUuid: student.owner || student.memberUuid,
    recipientLabel: `${student.title}的已授权监护人`,
    courses: lessons.map((r) => ({ uuid: r.uuid, title: r.title })),
    commitments,
    feedback: visibleFeedback(ctx).filter(
      (r) => feedbackRecord(r)?.studentUuid === studentUuid,
    ),
  };
}
export function feedbackIssues(ctx: DomainReadContext, row: Entity): string[] {
  const data = feedbackRecord(row),
    v = data?.versions.at(-1),
    issues: string[] = [];
  if (!v?.sentences.length) return ['请补充有依据的反馈正文'];
  if (!v.recipient.trim()) issues.push('尚未确认接收对象');
  for (const [index, sentence] of v.sentences.entries()) {
    const source = ctx.get(sentence.followUpUuid);
    if (!sentence.text.trim()) issues.push(`第${index + 1}句缺少正文`);
    if (!source || !commitment(source)?.confirmedAt)
      issues.push(`第${index + 1}句没有已确认履约依据`);
    else if (source.revision !== sentence.sourceRevision)
      issues.push(`第${index + 1}句引用的跟进已更新，请重新核实并保存版本`);
    if (sentence.evidence) {
      try {
        canonicalEvidence(ctx, sentence.evidence);
      } catch {
        issues.push(`第${index + 1}句的原始证据不可用`);
      }
    }
  }
  return [...new Set(issues)];
}
function linkedTasks(ctx: DomainReadContext, row: Entity) {
  return ctx.list('task').filter((t) => t.sourceUuid === row.uuid);
}
function changeTasks(ctx: DomainCommandContext, row: Entity, status?: string) {
  for (const task of linkedTasks(ctx, row)) {
    task.owner = row.owner;
    task.dueAt = row.dueAt;
    if (status) task.status = status;
    task.result = row.result;
    ctx.touch(task);
  }
}
function confirm(ctx: DomainCommandContext, input: Command) {
  preview(input);
  const row = follow(ctx, input.uuid),
    data = commitment(row)!;
  if (data.confirmedAt) return row;
  states(row, ['candidate', 'draft']);
  const student = profileSource(ctx, row);
  const member = ctx
    .members()
    .find(
      (m) => m.memberUuid === input.memberUuid && m.campus === student.campus,
    );
  if (!member) return fail('请选择同校区在职负责人');
  if (!row.evidence) return fail('缺少承诺原话证据，请先补充');
  const e = canonicalEvidence(ctx, row.evidence);
  const same = visibleFollowUps(ctx).find(
    (r) =>
      r.uuid !== row.uuid &&
      commitment(r)?.confirmedAt &&
      commitment(r)?.studentUuid === student.uuid &&
      commitment(r)?.candidateKey === data.candidateKey,
  );
  if (same) {
    data.exclusionReason = '同一来源承诺已确认，保留候选并关联正式记录';
    row.status = 'excluded';
    row.fields = { ...row.fields, duplicateOf: same.uuid };
    save(ctx, row, data, '重复候选已合并至正式承诺');
    return same;
  }
  if (input.fields?.recipient !== data.recipient)
    return fail('承诺对象须为该学生已授权监护人');
  if (!input.dueAt && input.fields?.missingDeadlineConfirmed !== 'true')
    return fail('请确认期限，或明确登记待补期限');
  row.dueAt = input.dueAt ? date(input.dueAt, '承诺期限') : undefined;
  if (
    row.dueAt &&
    Date.parse(row.dueAt) < Date.parse(ctx.now) &&
    !input.note?.trim()
  )
    return fail('确认已过期限的承诺需填写核实说明');
  row.owner = member.memberUuid;
  student.owner = member.memberUuid;
  ctx.touch(student);
  row.status = 'confirmed';
  row.evidence = e;
  row.sourceUuid = student.uuid;
  row.memberUuid = student.memberUuid;
  data.originalDueAt ||= row.dueAt;
  data.confirmedAt = ctx.now;
  data.confirmedBy = ctx.identity.memberUuid;
  row.note = input.note?.trim() || '人工核实承诺、服务对象及期限';
  save(ctx, row, data, '服务承诺已确认');
  if (!linkedTasks(ctx, row).length) ctx.task(row, `履行承诺：${row.title}`);
  return row;
}
function feedback(ctx: DomainReadContext, id: string | undefined) {
  const row = entity(ctx, id, 'feedback');
  if (!validFeedback(ctx, row)) return fail('反馈资料未就绪', 'CONFLICT');
  return row;
}
function version(
  ctx: DomainCommandContext,
  sentences: FeedbackSentence[],
  recipient: string,
  number: number,
): FeedbackVersion {
  return {
    uuid: crypto.randomUUID(),
    number,
    createdAt: ctx.now,
    author: ctx.identity.memberUuid,
    recipient,
    sentences,
  };
}
function migrate(state: Snapshot) {
  state.domainData ||= {};
  state.domainData.student ||= {};
  const students = state.entities.filter((r) => r.kind === 'student');
  // Upgrade known legacy records without inventing an association or claiming a confirmed promise.
  for (const row of state.entities.filter(
    (r) => r.kind === 'followUp' && !commitment(r),
  )) {
    const student = students.find(
      (s) => s.uuid === row.fields?.studentUuid && s.campus === row.campus,
    );
    if (!student) continue;
    const originUuid = row.sourceUuid;
    row.sourceUuid = student.uuid;
    write(row, {
      schema: 1,
      studentUuid: student.uuid,
      originUuid,
      candidateKey: row.fields?.analysisCandidateUuid || row.uuid,
      recipient: `${student.title}的已授权监护人`,
      confirmedAt: ['confirmed', 'inProgress', 'completed'].includes(row.status)
        ? row.updatedAt
        : undefined,
      confirmedBy: ['confirmed', 'inProgress', 'completed'].includes(row.status)
        ? 'legacy-confirmation'
        : undefined,
      originalDueAt: row.dueAt,
      contacts: row.result
        ? [
            {
              uuid: seedUuid(89000 + state.entities.indexOf(row)),
              at: row.updatedAt,
              method: 'message',
              result: row.result,
              nextStep: '历史记录待核实',
              actor: 'legacy-confirmation',
            },
          ]
        : [],
      extensions: [],
    });
    if (row.status === 'draft') row.status = 'candidate';
    row.revision++;
  }
  for (const row of state.entities.filter(
    (r) => r.kind === 'feedback' && !feedbackRecord(r),
  )) {
    const source = state.entities.find(
      (r) =>
        r.uuid === (row.fields?.followUpUuid || row.sourceUuid) &&
        r.kind === 'followUp',
    );
    const data = source && commitment(source);
    if (!source || !data?.confirmedAt || !row.fields?.body) continue;
    const oldStatus = row.status;
    row.sourceUuid = source.uuid;
    row.campus = source.campus;
    row.memberUuid = source.memberUuid;
    row.owner = source.owner;
    row.evidence = source.evidence;
    write(row, {
      schema: 1,
      studentUuid: data.studentUuid,
      versions: [
        {
          uuid: seedUuid(88000 + state.entities.indexOf(row)),
          number: 1,
          author: 'legacy-author',
          createdAt: row.updatedAt,
          recipient: data.recipient,
          sentences: [
            {
              uuid: seedUuid(87000 + state.entities.indexOf(row)),
              text: row.fields.body,
              followUpUuid: source.uuid,
              sourceRevision: source.revision,
              evidence: source.evidence,
            },
          ],
        },
      ],
      approvals: [],
      receipts: [],
    });
    // An old status string is not a version-bound approval or delivery receipt.
    row.fields = { ...row.fields, legacyFeedbackStatus: oldStatus };
    row.status = 'draft';
    row.revision++;
  }
  if (
    !students.length ||
    state.entities.some((r) => r.fields?.studentSeed === 'v1')
  )
    return;
  const now = state.now;
  for (let i = 0; i < 15; i++) {
    const student = students[i % students.length];
    const rec = state.entities.find(
      (r) =>
        r.kind === 'recording' &&
        r.campus === student.campus &&
        r.fixture === 1,
    );
    const transcript = rec?.versions?.at(-1),
      seg = transcript?.segments.find((s) => s.text.includes('本周五'));
    if (!rec || !transcript || !seg) continue;
    const advisor = state.entities.find(
      (r) =>
        r.kind === 'teacher' &&
        r.campus === student.campus &&
        r.status === 'active',
    );
    student.owner ||= advisor?.memberUuid || student.memberUuid;
    const completed = i < 8,
      candidate = i >= 12;
    const dueAt =
      i === 11 ? '2026-10-20T18:00:00+08:00' : '2026-10-23T18:00:00+08:00';
    const evidence = {
      recordingUuid: rec.uuid,
      transcriptVersionUuid: transcript.uuid,
      startMs: seg.startMs,
      endMs: seg.endMs,
      quote: seg.text,
    };
    const data: CommitmentCandidate = {
      schema: 1,
      studentUuid: student.uuid,
      originUuid: rec.uuid,
      candidateKey: `${student.uuid}:${rec.uuid}:${transcript.uuid}:${seg.startMs}:${seg.endMs}`,
      originalDueAt: candidate ? undefined : dueAt,
      recipient: `${student.title}的已授权监护人`,
      confirmedAt: candidate ? undefined : now,
      confirmedBy: candidate ? undefined : 'demo-confirmation',
      contacts: completed
        ? [
            {
              uuid: seedUuid(82000 + i),
              at: now,
              method: 'phone',
              result: '已核对学习建议并向授权监护人说明，约定下次沟通时间。',
              nextStep: '下次课后核实练习完成情况',
              actor: 'demo-confirmation',
            },
          ]
        : [],
      extensions: [],
    };
    const row: Entity = {
      uuid: seedUuid(81000 + i),
      kind: 'followUp',
      title: `${student.title} · 学习建议反馈`,
      campus: student.campus,
      memberUuid: student.memberUuid,
      owner: student.owner,
      status: candidate ? 'candidate' : completed ? 'completed' : 'confirmed',
      createdAt: now,
      updatedAt: now,
      revision: 1,
      sourceUuid: student.uuid,
      dueAt: candidate ? undefined : dueAt,
      note: '人工确认服务材料；虚构演示记录',
      result: completed ? data.contacts[0].result : undefined,
      evidence,
      fields: {
        studentUuid: student.uuid,
        studentSeed: 'v1',
        [key]: JSON.stringify(data),
      },
    };
    state.entities.push(row);
    if (!candidate && !completed)
      state.entities.push({
        ...row,
        uuid: seedUuid(83000 + i),
        kind: 'task',
        title: `履行承诺：${row.title}`,
        status: 'pending',
        sourceUuid: row.uuid,
        evidence: undefined,
        fields: {},
      });
    if (i < 3) {
      const v: FeedbackVersion = {
        uuid: seedUuid(85000 + i),
        number: 1,
        createdAt: now,
        author: 'demo-author',
        recipient: data.recipient,
        sentences: [
          {
            uuid: seedUuid(86000 + i),
            text: row.result!,
            followUpUuid: row.uuid,
            sourceRevision: row.revision,
            evidence,
          },
        ],
      };
      const f: FeedbackRecord = {
        schema: 1,
        studentUuid: student.uuid,
        versions: [v],
        approvals: [],
        receipts: [],
      };
      state.entities.push({
        ...row,
        uuid: seedUuid(84000 + i),
        kind: 'feedback',
        title: `${student.title} · 服务反馈`,
        status: 'draft',
        sourceUuid: row.uuid,
        evidence,
        fields: { studentUuid: student.uuid, [key]: JSON.stringify(f) },
      });
    }
  }
}
const confirmCommand = {
  roles: staff,
  kind: 'followUp' as const,
  label: '确认服务承诺',
  execute: confirm,
};
export const studentDomain: DomainModule = {
  namespace: 'student',
  version: 2,
  resource: 'service',
  pages: [
    {
      id: 'S01',
      path: '/student/:studentUuid',
      resource: 'student',
      parentPath: '/service/follow-up',
      returnTo: '/service/follow-up',
    },
    {
      id: 'S02',
      path: '/service/follow-up',
      resource: 'service',
      parentPath: '/service/follow-up',
      returnTo: '/service/follow-up',
    },
    {
      id: 'S03',
      path: '/service/feedback',
      resource: 'feedback',
      parentPath: '/service/follow-up',
      returnTo: '/service/follow-up',
    },
  ],
  migrations: [
    { version: 1, up: migrate },
    {
      version: 2,
      up(state) {
        for (const row of state.entities.filter(
          (r) => r.kind === 'followUp' && r.fields?.studentSeed === 'v1',
        )) {
          const validOwner = state.entities.some(
            (r) =>
              r.kind === 'teacher' &&
              r.campus === row.campus &&
              r.memberUuid === row.owner &&
              r.status === 'active',
          );
          if (validOwner) continue;
          const member = state.entities.find(
            (r) =>
              r.kind === 'teacher' &&
              r.campus === row.campus &&
              r.status === 'active',
          );
          if (!member) continue;
          row.owner = member.memberUuid;
          row.revision++;
          const student = state.entities.find(
            (r) => r.uuid === commitment(row)?.studentUuid,
          );
          if (student && !student.owner) student.owner = member.memberUuid;
          // Only untouched seed drafts follow this fixture metadata correction. User versions remain immutable.
          for (const document of state.entities.filter(
            (r) => r.kind === 'feedback' && r.sourceUuid === row.uuid,
          )) {
            const data = feedbackRecord(document);
            if (
              !data ||
              data.versions.length !== 1 ||
              data.versions[0].author !== 'demo-author' ||
              data.approvals.length ||
              data.receipts.length
            )
              continue;
            for (const sentence of data.versions[0].sentences)
              sentence.sourceRevision = row.revision;
            document.owner = member.memberUuid;
            write(document, data);
            document.revision++;
          }
        }
      },
    },
  ],
  query(ctx, input: QueryInput) {
    let rows = input.kinds?.includes('feedback')
      ? visibleFeedback(ctx)
      : visibleFollowUps(ctx);
    if (input.keyword)
      rows = rows.filter((r) => `${r.title}${r.note}`.includes(input.keyword!));
    if (input.status) rows = rows.filter((r) => r.status === input.status);
    if (input.memberUuid)
      rows = rows.filter((r) => r.owner === input.memberUuid);
    return paginate(rows, input, ctx.revision);
  },
  search(ctx) {
    // The G0 work provider already indexes follow-ups; contribute only versioned feedback here.
    return visibleFeedback(ctx).map((row) => ({
      id: `student:${row.uuid}`,
      entityUuid: row.uuid,
      title: row.title,
      text: `${row.title} ${
        feedbackRecord(row)
          ?.versions.at(-1)
          ?.sentences.map((s) => s.text)
          .join(' ') || ''
      }`,
      kind: row.kind,
      memberUuid: row.memberUuid,
      occurredAt: row.createdAt,
      evidence: row.evidence,
    }));
  },
  metrics(ctx) {
    const m = serviceMetrics(ctx);
    return [
      {
        uuid: 'student:fulfilment',
        scope: ctx.context.campus,
        asOf: ctx.now,
        metric: '正式服务承诺完成',
        numerator: m.completed,
        denominator: m.confirmed || null,
        unit: '项',
        sourceUuids: visibleFollowUps(ctx)
          .filter((r) => commitment(r)?.confirmedAt)
          .map((r) => r.uuid),
        definitionVersion: 'S-C1.1',
      },
    ];
  },
  suggestions(ctx) {
    return visibleFollowUps(ctx)
      .filter((r) => ['candidate', 'draft'].includes(r.status) && !!r.evidence)
      .map((r) => ({
        id: `student:${r.uuid}:${r.revision}`,
        sourceUuid: r.uuid,
        action: 'student.confirm',
        label: '核实服务承诺并安排责任人',
        fields: { recipient: commitment(r)!.recipient },
      }));
  },
  commands: {
    candidate: {
      roles: staff,
      kind: 'student',
      label: '登记服务候选',
      execute(ctx, input) {
        preview(input);
        const student = entity(ctx, input.uuid, 'student');
        if (!input.evidence) return fail('请选择来源录音片段');
        const evidence = canonicalEvidence(ctx, input.evidence);
        const recording = entity(ctx, evidence.recordingUuid, 'recording');
        if (recording.campus !== student.campus)
          return fail('来源与学生须在同一校区');
        const candidateKey = `${student.uuid}:${evidence.recordingUuid}:${evidence.transcriptVersionUuid}:${evidence.startMs}:${evidence.endMs}`;
        const existing = visibleFollowUps(ctx).find(
          (r) => commitment(r)?.candidateKey === candidateKey,
        );
        if (existing) return existing;
        const row = ctx.append('followUp', need(input.title, '承诺摘要'), {
          status: 'candidate',
          campus: student.campus,
          memberUuid: student.memberUuid,
          owner: ctx.context.viewMemberUuid,
          sourceUuid: student.uuid,
          evidence,
          note: need(input.note, '关联与核实说明'),
          fields: { studentUuid: student.uuid },
        });
        write(row, {
          schema: 1,
          studentUuid: student.uuid,
          originUuid: recording.uuid,
          candidateKey,
          recipient: `${student.title}的已授权监护人`,
          contacts: [],
          extensions: [],
        });
        ctx.notify(row, '新增服务候选，待人工确认');
        return row;
      },
    },
    confirm: confirmCommand,
    exclude: {
      roles: staff,
      kind: 'followUp',
      label: '排除服务候选',
      execute(ctx, input) {
        preview(input);
        const row = follow(ctx, input.uuid),
          data = commitment(row)!;
        states(row, ['candidate', 'draft']);
        data.exclusionReason = need(input.note, '排除原因');
        row.status = 'excluded';
        return save(ctx, row, data, '服务候选已人工排除');
      },
    },
    contact: {
      roles: staff,
      kind: 'followUp',
      label: '登记服务联系',
      execute(ctx, input) {
        preview(input);
        const row = follow(ctx, input.uuid),
          data = commitment(row)!;
        owner(ctx, row);
        states(row, ['confirmed', 'inProgress']);
        const method = input.fields?.method;
        if (!['phone', 'meeting', 'message'].includes(method || ''))
          return fail('请选择联系渠道');
        data.contacts.push({
          uuid: crypto.randomUUID(),
          at: ctx.now,
          method: method as 'phone',
          result: need(input.note, '联系结果'),
          nextStep: need(input.fields?.nextStep, '后续安排'),
          actor: ctx.identity.memberUuid,
        });
        row.status = 'inProgress';
        save(ctx, row, data, '联系结果已登记');
        changeTasks(ctx, row, 'inProgress');
        return row;
      },
    },
    extend: {
      roles: staff,
      kind: 'followUp',
      label: '调整服务期限',
      execute(ctx, input) {
        preview(input);
        const row = follow(ctx, input.uuid),
          data = commitment(row)!;
        owner(ctx, row);
        states(row, ['confirmed', 'inProgress']);
        const to = date(input.dueAt, '新期限');
        if (
          Date.parse(to) <= Date.parse(ctx.now) ||
          (row.dueAt && Date.parse(to) <= Date.parse(row.dueAt))
        )
          return fail('延期须晚于当前时间及原期限');
        data.extensions.push({
          at: ctx.now,
          from: row.dueAt,
          to,
          reason: need(input.note, '延期原因'),
          actor: ctx.identity.memberUuid,
        });
        row.dueAt = to;
        save(ctx, row, data, '服务期限已调整，保留原约定');
        changeTasks(ctx, row);
        return row;
      },
    },
    assign: {
      roles: managers,
      kind: 'followUp',
      label: '分派服务负责人',
      execute(ctx, input) {
        preview(input);
        const row = follow(ctx, input.uuid),
          data = commitment(row)!;
        states(row, ['confirmed', 'inProgress']);
        const member = ctx
          .members()
          .find(
            (m) => m.memberUuid === input.memberUuid && m.campus === row.campus,
          );
        if (!member) return fail('请选择同校区在职负责人');
        need(input.note, '分派说明');
        row.owner = member.memberUuid;
        const student = profileSource(ctx, row);
        student.owner = member.memberUuid;
        ctx.touch(student);
        save(ctx, row, data, '服务责任人已调整');
        changeTasks(ctx, row);
        return row;
      },
    },
    complete: {
      roles: staff,
      kind: 'followUp',
      label: '完成服务履约',
      execute(ctx, input) {
        preview(input);
        const row = follow(ctx, input.uuid),
          data = commitment(row)!;
        owner(ctx, row);
        states(row, ['confirmed', 'inProgress']);
        if (!data.contacts.length) return fail('请先登记联系结果及后续安排');
        row.result = need(input.note, '履行材料与结果');
        row.status = 'completed';
        save(ctx, row, data, '服务承诺已履行');
        changeTasks(ctx, row, 'completed');
        return row;
      },
    },
    generate: {
      roles: staff,
      kind: 'followUp',
      label: '生成反馈草稿',
      execute(ctx, input) {
        preview(input);
        const row = follow(ctx, input.uuid),
          data = commitment(row)!;
        states(row, ['completed']);
        need(row.result, '已确认履行结果');
        const existing = visibleFeedback(ctx).find(
          (f) => f.fields?.generationSource === `${row.uuid}:${row.revision}`,
        );
        if (existing) return existing;
        const v = version(
          ctx,
          [
            {
              uuid: crypto.randomUUID(),
              text: row.result!,
              followUpUuid: row.uuid,
              sourceRevision: row.revision,
              evidence: row.evidence,
            },
          ],
          data.recipient,
          1,
        );
        const feedback = ctx.append(
          'feedback',
          `${entity(ctx, data.studentUuid, 'student').title} · 服务反馈`,
          {
            campus: row.campus,
            memberUuid: row.memberUuid,
            owner: row.owner,
            sourceUuid: row.uuid,
            status: 'draft',
            evidence: row.evidence,
            fields: {
              studentUuid: data.studentUuid,
              generationSource: `${row.uuid}:${row.revision}`,
            },
          },
        );
        write(feedback, {
          schema: 1,
          studentUuid: data.studentUuid,
          versions: [v],
          approvals: [],
          receipts: [],
        });
        ctx.notify(feedback, '反馈草稿已生成，待编辑审核');
        return feedback;
      },
    },
    edit: {
      roles: staff,
      kind: 'feedback',
      label: '保存反馈新版本',
      execute(ctx, input) {
        preview(input);
        const row = feedback(ctx, input.uuid),
          data = feedbackRecord(row)!;
        owner(ctx, row);
        const current = data.versions.at(-1)!;
        let requested: unknown;
        try {
          requested = JSON.parse(input.fields?.sentences || '[]');
        } catch {
          return fail('反馈句子格式无效');
        }
        if (
          !Array.isArray(requested) ||
          !requested.length ||
          requested.length > 30
        )
          return fail('请保留1至30条反馈内容');
        const sentences = requested.map(
          (s: { text?: string; followUpUuid?: string }) => {
            if (!s || typeof s !== 'object') return fail('反馈句子格式无效');
            const source = follow(ctx, s.followUpUuid);
            if (
              source.uuid !== row.sourceUuid ||
              commitment(source)!.studentUuid !== data.studentUuid ||
              !commitment(source)!.confirmedAt
            )
              return fail('仅可引用该学生已确认的服务材料');
            return {
              uuid: crypto.randomUUID(),
              text: need(s.text, '反馈正文'),
              followUpUuid: source.uuid,
              sourceRevision: source.revision,
              evidence: source.evidence,
            };
          },
        );
        const recipient = need(input.fields?.recipient, '接收对象');
        if (recipient !== commitment(follow(ctx, row.sourceUuid))!.recipient)
          return fail('接收对象须为本次已授权监护人');
        const v = version(ctx, sentences, recipient, current.number + 1);
        data.versions.push(v);
        data.submittedVersionUuid = undefined;
        data.submittedBy = undefined;
        data.submittedViewMemberUuid = undefined;
        row.status = 'draft';
        return save(ctx, row, data, '反馈新版本已保存，原审核不再适用');
      },
    },
    submit: {
      roles: staff,
      kind: 'feedback',
      label: '提交反馈审核',
      execute(ctx, input) {
        preview(input);
        const row = feedback(ctx, input.uuid),
          data = feedbackRecord(row)!;
        owner(ctx, row);
        states(row, ['draft', 'returned']);
        const issues = feedbackIssues(ctx, row);
        if (issues.length) return fail(issues.join('；'));
        const v = data.versions.at(-1)!;
        data.submittedVersionUuid = v.uuid;
        data.submittedBy = ctx.identity.memberUuid;
        data.submittedViewMemberUuid = ctx.context.viewMemberUuid;
        row.status = 'pendingApproval';
        return save(ctx, row, data, '反馈已提交审核');
      },
    },
    review: {
      roles: managers,
      kind: 'feedback',
      label: '审核反馈内容',
      execute(ctx, input) {
        preview(input);
        const row = feedback(ctx, input.uuid),
          data = feedbackRecord(row)!;
        states(row, ['pendingApproval']);
        const v = data.versions.at(-1)!;
        if (data.submittedVersionUuid !== v.uuid)
          return fail('正文已改变，请重新送审', 'CONFLICT');
        const decision = input.fields?.decision;
        if (!['approved', 'returned'].includes(decision || ''))
          return fail('请选择审核结论');
        if (feedbackIssues(ctx, row).length && decision === 'approved')
          return fail('引用资料已变化，请退回核实');
        data.approvals.push({
          uuid: crypto.randomUUID(),
          versionUuid: v.uuid,
          decision: decision as 'approved',
          actor: ctx.identity.memberUuid,
          viewMemberUuid: ctx.context.viewMemberUuid,
          at: ctx.now,
          note: need(input.note, '审核意见'),
        });
        row.status = decision === 'approved' ? 'approved' : 'returned';
        return save(
          ctx,
          row,
          data,
          decision === 'approved' ? '反馈审核通过' : '反馈已退回修改',
        );
      },
    },
    send: {
      roles: staff,
      kind: 'feedback',
      label: '模拟发送审核反馈',
      execute(ctx, input) {
        preview(input);
        const row = feedback(ctx, input.uuid),
          data = feedbackRecord(row)!;
        const v = data.versions.at(-1)!;
        const receipt = data.receipts.find((r) => r.versionUuid === v.uuid);
        if (receipt) return row;
        states(row, ['approved']);
        const approval = data.approvals.at(-1);
        if (
          approval?.versionUuid !== v.uuid ||
          approval.decision !== 'approved' ||
          feedbackIssues(ctx, row).length
        )
          return fail('必须审核当前有效版本后才可发送', 'INVALID_TRANSITION');
        if (
          input.fields?.recipient !== v.recipient ||
          input.fields?.versionUuid !== v.uuid ||
          input.fields?.acknowledged !== 'true'
        )
          return fail('请预览并确认接收对象和最终版本');
        if (input.fields?.simulateFailure === 'true')
          return fail(
            '模拟发送暂时失败，未生成收据；请保留版本重试',
            'UNAVAILABLE',
          );
        data.receipts.push({
          uuid: crypto.randomUUID(),
          businessKey: `${row.uuid}:${v.uuid}`,
          versionUuid: v.uuid,
          recipient: v.recipient,
          at: ctx.now,
          actor: ctx.identity.memberUuid,
          channel: 'simulated',
        });
        row.status = 'sentSimulated';
        return save(ctx, row, data, '反馈已模拟发送，收据已保存');
      },
    },
  },
};

// Integration hook: reject legacy generic mutation paths for this domain's versioned objects.
export function guardStudentLegacyCommand(
  row: Entity | undefined,
  action: string,
) {
  if (
    row?.kind === 'followUp' &&
    commitment(row) &&
    !commitment(row)?.confirmedAt &&
    ['workspaceTask', 'task'].includes(action)
  )
    throw new DomainFault(
      'INVALID_TRANSITION',
      '请先人工确认承诺，再建立正式服务任务',
    );
  if (
    row?.fields?.studentServiceV1 &&
    ['moduleUpdate', 'moduleTransition', 'moduleClone'].includes(action)
  ) {
    throw new DomainFault(
      'INVALID_TRANSITION',
      '此服务记录须通过专用履约与反馈流程操作',
    );
  }
}
